import { DialogComponent } from '@theme/dialog';
import { decideTarget, buildCrossShopUrl, suppressionKey } from '@theme/geo-ip-target';
import { hasConsentDecision } from '@theme/analytics-utils';

const COOKIE_NAME = 'jm_geo_ip_dismissed';
const GATE_TIMEOUT_MS = 15000;

/**
 * GEO IP messaging banner. Waits for the age gate + cookie banner, detects the
 * visitor's country via Shopify's browsing_context_suggestions.json, and — if a
 * more-local shop/market exists — shows a dismissible suggestion pop-up. Never
 * auto-redirects.
 *
 * @typedef {object} GeoIpRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLInputElement} countryInput
 * @extends DialogComponent
 */
class GeoIpBannerComponent extends DialogComponent {
  /** @type {ReturnType<typeof decideTarget>} */
  #target = null;

  /** @type {(() => void) | null} */
  #sequenceCleanup = null;
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();
    if (this.dataset.enabled !== 'true') return;
    if (navigator.webdriver) return; // bot exclusion
    this.#run().catch(() => {}); // fail silent
  }

  async #run() {
    const payload = this.#readPayload();
    if (!payload) return;

    this.#abortController = new AbortController();

    await this.#awaitSequence();
    if (!this.isConnected) return;

    const detected = await this.#detectCountry();
    if (!this.isConnected || !detected) return;

    const target = decideTarget(
      payload.map,
      detected,
      this.dataset.currentCountry || '',
      payload.fallback,
      window.location.host
    );
    if (!target) return;
    if (this.#isSuppressed(suppressionKey(target))) return;

    this.#target = target;
    if (!this.isConnected) return;
    this.showDialog();
    this.addEventListener('dialog:close', this.#onClose);
  }

  /** Reads the embedded JSON payload. */
  #readPayload() {
    const el = this.querySelector('script[data-geo-ip-map]');
    if (!el || !el.textContent) return null;
    try {
      const data = JSON.parse(el.textContent);
      return { map: data.map || {}, fallback: data.fallback || { url: '' } };
    } catch {
      return null;
    }
  }

  /**
   * Resolves once BOTH the cookie banner (OneTrust) and the age gate are done.
   * Age Gate > Cookie Banner > Location banner.
   *
   * The timeout is only a safety net for a *missing* signal — when neither a
   * cookie banner nor an age gate is present on the page, "not done" would
   * otherwise never flip. When either mechanism IS present, "not done" means the
   * visitor simply hasn't decided yet; force-resolving then would render the
   * pop-up over the still-open banner — the exact race the sequencing prevents.
   * So the timeout is armed only when neither mechanism is present; otherwise we
   * wait for the real completion signal (and never show if the visitor abandons).
   */
  #awaitSequence() {
    return new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        document.removeEventListener('OneTrustGroupsUpdated', check);
        clearInterval(poll);
        clearTimeout(timer);
        this.#sequenceCleanup = null;
        resolve();
      };
      const check = () => {
        if (this.#cookieBannerDone() && this.#ageGateDone()) finish();
      };
      const poll = setInterval(check, 300);
      const hasCookieBanner = !!document.querySelector('script[data-domain-script]');
      const hasAgeGate = !!this.dataset.ageGateCookie;
      const timer = hasCookieBanner || hasAgeGate ? null : setTimeout(finish, GATE_TIMEOUT_MS);
      document.addEventListener('OneTrustGroupsUpdated', check);
      this.#sequenceCleanup = finish;
      check();
    });
  }

  /**
   * Cookie banner is "done" when the visitor has actually made a consent choice,
   * or when no OneTrust script is present at all (e.g. the cookie banner is
   * disabled). `OnetrustActiveGroups` is NOT used: OneTrust pre-populates it with
   * the always-on category (C0001) on SDK load, before any decision, so it would
   * let this pop-up appear over a still-open cookie banner. Instead defer to the
   * theme's canonical helper, which keys off the OptanonAlertBoxClosed cookie.
   */
  #cookieBannerDone() {
    const hasOneTrust = !!document.querySelector('script[data-domain-script]');
    if (!hasOneTrust) return true;
    return hasConsentDecision();
  }

  /**
   * Age gate is "done" when its configured cookie is present. When no cookie
   * name is configured, the age gate isn't gated on (skip).
   */
  #ageGateDone() {
    const name = this.dataset.ageGateCookie;
    if (!name) return true;
    return document.cookie.split('; ').some((c) => c.startsWith(`${name}=`));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#sequenceCleanup?.();
    this.#abortController?.abort();
    this.removeEventListener('dialog:close', this.#onClose);
  }

  /**
   * Detect the visitor's country via Shopify's native suggestion endpoint.
   * Returns the ISO code (e.g. "GB") or '' on any failure/empty.
   */
  async #detectCountry() {
    try {
      const res = await fetch('/browsing_context_suggestions.json', {
        headers: { Accept: 'application/json' },
        signal: this.#abortController?.signal,
      });
      if (!res.ok) return '';
      const data = await res.json();
      return data?.detected_values?.country?.handle || '';
    } catch {
      return '';
    }
  }

  /** "Go to local shop": navigate (cross / fallback) or submit form (within). */
  go = (event) => {
    event?.preventDefault();
    const target = this.#target;
    if (!target) return;

    this.#suppress(suppressionKey(target));

    // `x` marks a market still on its old, non-Shopify site (external in the GCE
    // payload). It serves none of our paths, so it gets the bare URL — no deep
    // link, no parameters.
    //
    // Decided before the `within` branch: such a market can sit in this very shop
    // — Austria, Czechia, Slovakia, Greece, Poland and the UK are all markets of
    // the EU shop — and is then reported as `within`. Submitting the country form
    // would keep the visitor here instead of sending them to the site that
    // actually serves them. `u` rather than `url`, because only a `cross` target
    // carries `url`.
    const externalUrl = target.x ? target.u || target.url : '';
    if (externalUrl) {
      window.location.assign(externalUrl);
      return;
    }

    if (target.kind === 'within') {
      this.#submitCountryForm(target.country);
      return;
    }
    if (target.kind === 'cross') {
      window.location.assign(this.#deepLink(target.url, target.country));
      return;
    }
    // fallback
    window.location.assign(target.url);
  };

  /** Build a deep-linked cross-shop URL from the current location. */
  #deepLink(base, country) {
    return buildCrossShopUrl(base, {
      root: window.Shopify?.routes?.root ?? '/',
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
      country,
    });
  }

  /** Dismiss ("Stay here anyways" / close X). Base Escape/click-outside also close. */
  dismiss = (event) => {
    event?.preventDefault();
    this.closeDialog();
  };

  /** Any close (button, Escape, click-outside) records the per-target dismissal. */
  #onClose = () => {
    if (this.#target) this.#suppress(suppressionKey(this.#target));
  };

  /** Submit the native Shopify localization form for a same-shop market. */
  #submitCountryForm(countryCode) {
    const { countryInput } = this.refs;
    const form = countryInput?.closest('form');
    if (!countryInput || !form) return;
    countryInput.value = countryCode;
    form.submit();
  }

  #cookieDays() {
    const n = Number(this.dataset.suppressionDays);
    return Number.isFinite(n) && n > 0 ? n : 30;
  }

  /** Read the suppression cookie as a Set of dismissed target keys. */
  #dismissedSet() {
    const raw = document.cookie
      .split('; ')
      .find((c) => c.startsWith(`${COOKIE_NAME}=`))
      ?.split('=')[1];
    if (!raw) return new Set();
    try {
      const parsed = JSON.parse(decodeURIComponent(raw));
      return Array.isArray(parsed) ? new Set(parsed) : new Set();
    } catch {
      return new Set();
    }
  }

  #isSuppressed(key) {
    return key ? this.#dismissedSet().has(key) : false;
  }

  /** Add a target key to the suppression cookie (per-target, 30-day default). */
  #suppress(key) {
    if (!key) return;
    const set = this.#dismissedSet();
    if (set.has(key)) return;
    set.add(key);
    const value = encodeURIComponent(JSON.stringify([...set]));
    const maxAge = this.#cookieDays() * 24 * 60 * 60;
    document.cookie = `${COOKIE_NAME}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
  }
}

if (!customElements.get('geo-ip-banner')) {
  customElements.define('geo-ip-banner', GeoIpBannerComponent);
}
